import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { FormFieldWrapper } from '@/components/forms/FormFieldWrapper';
import { UseFormReturn } from 'react-hook-form';
import { ContactPartnerSchemaType } from '@/hooks/useContactPartnerForm';

interface FormFieldsProps {
  form: UseFormReturn<ContactPartnerSchemaType>;
  tForms: (key: string) => string;
  contactTypeOptions: Array<{ value: string; label: string }>;
  languageOptions: Array<{ value: string; label: string }>;
  titleOptions: Array<{ value: string; label: string }>;
}

/**
 * Contact Partner Form Fields Component
 * Reusable form fields following DRY principle and component composition
 */
export function ContactPartnerFormFields({
  form,
  tForms,
  contactTypeOptions,
  languageOptions,
  titleOptions,
}: FormFieldsProps) {
  return (
    <>
      {/* Row 1: Contact Type & Language */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormFieldWrapper
          name='contactType'
          label={tForms('contactPartner.fields.contactType')}
          required
          error={form.formState.errors.contactType?.message}
        >
          <Select
            value={form.watch('contactType')}
            onValueChange={value => form.setValue('contactType', value)}
          >
            <SelectTrigger className='w-full'>
              <SelectValue
                placeholder={tForms(
                  'contactPartner.placeholders.selectContactType'
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {contactTypeOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormFieldWrapper>

        <FormFieldWrapper
          name='language'
          label={tForms('contactPartner.fields.language')}
          required
          error={form.formState.errors.language?.message}
        >
          <Select
            value={form.watch('language')}
            onValueChange={value => form.setValue('language', value)}
          >
            <SelectTrigger className='w-full'>
              <SelectValue
                placeholder={tForms(
                  'contactPartner.placeholders.selectLanguage'
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {languageOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormFieldWrapper>
      </div>

      {/* Row 2: Function & Title */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormFieldWrapper
          name='function'
          label={tForms('contactPartner.fields.function')}
          required
          error={form.formState.errors.function?.message}
        >
          <Input
            id='function'
            type='text'
            {...form.register('function')}
            placeholder={tForms('contactPartner.placeholders.enterFunction')}
            className='w-full'
          />
        </FormFieldWrapper>

        <FormFieldWrapper
          name='title'
          label={tForms('contactPartner.fields.title')}
          required
          error={form.formState.errors.title?.message}
        >
          <Select
            value={form.watch('title')}
            onValueChange={value => form.setValue('title', value)}
          >
            <SelectTrigger className='w-full'>
              <SelectValue
                placeholder={tForms('contactPartner.placeholders.selectTitle')}
              />
            </SelectTrigger>
            <SelectContent>
              {titleOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormFieldWrapper>
      </div>

      {/* Row 3: First Name & Last Name */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormFieldWrapper
          name='firstName'
          label={tForms('contactPartner.fields.firstName')}
          required
          error={form.formState.errors.firstName?.message}
        >
          <Input
            id='firstName'
            type='text'
            {...form.register('firstName')}
            placeholder={tForms('contactPartner.placeholders.enterFirstName')}
            className='w-full'
          />
        </FormFieldWrapper>

        <FormFieldWrapper
          name='lastName'
          label={tForms('contactPartner.fields.lastName')}
          required
          error={form.formState.errors.lastName?.message}
        >
          <Input
            id='lastName'
            type='text'
            {...form.register('lastName')}
            placeholder={tForms('contactPartner.placeholders.enterLastName')}
            className='w-full'
          />
        </FormFieldWrapper>
      </div>

      {/* Row 4: Representative Switch & Email */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormFieldWrapper
          name='isRepresentative'
          label={tForms('contactPartner.fields.isRepresentative')}
          error={form.formState.errors.isRepresentative?.message}
        >
          <div className='flex items-center space-x-3 pt-2'>
            <Switch
              id='isRepresentative'
              checked={form.watch('isRepresentative')}
              onCheckedChange={checked =>
                form.setValue('isRepresentative', checked)
              }
            />
            <span className='text-sm text-muted-foreground'>
              {form.watch('isRepresentative')
                ? tForms('contactPartner.fields.isRepresentativeYes')
                : tForms('contactPartner.fields.isRepresentativeNo')}
            </span>
          </div>
        </FormFieldWrapper>

        <FormFieldWrapper
          name='email'
          label={tForms('contactPartner.fields.email')}
          required
          error={form.formState.errors.email?.message}
        >
          <Input
            id='email'
            type='email'
            {...form.register('email')}
            placeholder={tForms('contactPartner.placeholders.enterEmail')}
            className='w-full'
          />
        </FormFieldWrapper>
      </div>

      {/* Row 5: Mobile & Phone */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormFieldWrapper
          name='mobile'
          label={tForms('contactPartner.fields.mobile')}
          error={form.formState.errors.mobile?.message}
        >
          <Input
            id='mobile'
            type='tel'
            {...form.register('mobile')}
            placeholder={tForms('contactPartner.placeholders.enterMobile')}
            className='w-full'
          />
        </FormFieldWrapper>

        <FormFieldWrapper
          name='phone'
          label={tForms('contactPartner.fields.phone')}
          required
          error={form.formState.errors.phone?.message}
        >
          <Input
            id='phone'
            type='tel'
            {...form.register('phone')}
            placeholder={tForms('contactPartner.placeholders.enterPhone')}
            className='w-full'
          />
        </FormFieldWrapper>
      </div>
    </>
  );
}
